import {
  IconAlertTriangle,
  IconInfoCircle,
  IconPhone,
} from "@tabler/icons-react";
import type { FC, ReactNode } from "react";
import { Dialog, Heading } from "react-aria-components";
import { tv } from "tailwind-variants";
import { Button, ButtonVariant } from "../Buttons/button.js";

export interface AlertDialogProps {
  variant?: AlertDialogVariant;
  title: ReactNode;
  children?: ReactNode;
  confirmText: ReactNode;
  cancelText?: ReactNode;
  isConfirming?: boolean;
  onConfirm?: (context: { close: () => void }) => void;
}

export const AlertDialogVariant = {
  /** Ask the user to confirm their action. */
  Confirm: "CONFIRM",
  /** Warn the user about the impact of their action. */
  Warning: "WARNING",
  /** Special variant to confirm hanging up / ending a call. */
  CallEnd: "CALL_END",
};

export type AlertDialogVariant =
  (typeof AlertDialogVariant)[keyof typeof AlertDialogVariant];

const icon = tv({
  slots: {
    circle:
      "flex size-12 shrink-0 items-center justify-center rounded-full sm:mx-0 sm:size-10",
    icon: "size-6",
  },
  variants: {
    variant: {
      [AlertDialogVariant.Confirm]: {
        circle: "bg-indigo-100",
        icon: "text-indigo-600",
      },
      [AlertDialogVariant.Warning]: {
        circle: "bg-red-100",
        icon: "text-destructive",
      },
      [AlertDialogVariant.CallEnd]: {
        circle: "bg-red-100",
        icon: "text-destructive",
      },
    },
  },
  defaultVariants: {
    variant: AlertDialogVariant.Confirm,
  },
});

const confirmButtonVariants: Record<AlertDialogVariant, ButtonVariant> = {
  [AlertDialogVariant.Confirm]: ButtonVariant.Primary,
  [AlertDialogVariant.Warning]: ButtonVariant.Warning,
  [AlertDialogVariant.CallEnd]: ButtonVariant.CallEnd,
};

const iconRenderers: Record<
  AlertDialogVariant,
  (props: { className: string }) => ReactNode
> = {
  [AlertDialogVariant.Confirm]: (props) => <IconInfoCircle {...props} />,
  [AlertDialogVariant.Warning]: (props) => <IconAlertTriangle {...props} />,
  [AlertDialogVariant.CallEnd]: (props) => <IconPhone {...props} />,
};

// TODO: Make more versatile so it can be used for non-destructive alerts.
export const AlertDialog: FC<AlertDialogProps> = (props) => {
  const { variant = AlertDialogVariant.Confirm, onConfirm } = props;

  return (
    <Dialog role="alertdialog" className="space-y-4 p-4 sm:p-6">
      {({ close }) => (
        <>
          <div className="flex flex-col items-center gap-4 sm:flex-row sm:items-start">
            <div className={icon({ variant }).circle()}>
              {iconRenderers[variant]?.({
                className: icon({ variant }).icon(),
              })}
            </div>

            <div className="space-y-4">
              <div className="space-y-2">
                <Heading
                  slot="title"
                  className="text-base font-semibold text-foreground"
                >
                  {props.title}
                </Heading>

                {props.children ? (
                  <div className="text-sm text-muted-foreground">
                    {props.children}
                  </div>
                ) : null}
              </div>

              <div className="flex items-center justify-end space-x-2">
                <Button slot="close" variant={ButtonVariant.Secondary}>
                  {props.cancelText ?? "Cancel"}
                </Button>

                <Button
                  variant={
                    confirmButtonVariants[variant] ?? ButtonVariant.Primary
                  }
                  isLoading={props.isConfirming}
                  isDisabled={!onConfirm}
                  onClick={onConfirm ? () => onConfirm({ close }) : undefined}
                >
                  {props.confirmText}
                </Button>
              </div>
            </div>
          </div>
        </>
      )}
    </Dialog>
  );
};
